"use client";

import { useParams, useNavigate } from "react-router-dom";
import {
  Flex,
  Heading,
  Text,
  Link,
  Collection,
  Card,
  Badge,
  Grid,
} from "@aws-amplify/ui-react";
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import { generateClient } from "aws-amplify/api";
import { Helmet } from "react-helmet";
import { PatternCreateForm } from "../../ui-components";
import { Schema } from "../../../amplify/data/resource";
import { hasAccess } from "../../acl/groups";
import { I18n } from "aws-amplify/utils";
import { FaArrowLeft, FaPencil, FaPlus } from "react-icons/fa6";
import LoadingDialog from "../../components/LoadingDialog";
import PaperComponent from "../../components/PaperComponent";

const client = generateClient<Schema>();

type DanceStyle = Schema["DanceStyle"]["type"];
type Pattern = Schema["Pattern"]["type"];

interface DanceStyleViewProps {
  groups: string[];
}

const DanceStyleView: React.FC<DanceStyleViewProps> = ({ groups }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [danceStyleRecord, setDanceStyle] = React.useState<DanceStyle>(
    {} as DanceStyle
  );
  const [patterns, setPatterns] = React.useState<Pattern[]>([]);
  const [open, setOpen] = React.useState(false);

  const [loadingMap, setLoadingMap] = React.useState<{
    [key: string]: boolean;
  }>({});

  const markLoading = (key: string, value: boolean) => {
    setLoadingMap((prev) => ({ ...prev, [key]: value }));
  };

  const onClickAddPattern = () => {
    setOpen(true);
  };

  const onCloseDialog = () => {
    setOpen(false);
  };

  React.useEffect(() => {
    markLoading("danceStyle", true);
    const danceStyleSub = client.models.DanceStyle.observeQuery({
      filter: {
        id: { eq: id },
      },
    }).subscribe({
      next: ({ items }) => {
        setDanceStyle(items[0]);
        markLoading("danceStyle", false);
      },
    });
    const patternQueryStatus = hasAccess(groups, "patterns", "update")
      ? {}
      : { status: { eq: true } };

    markLoading("patterns", true);
    const patternsSub = client.models.Pattern.observeQuery({
      filter: {
        danceStylePatternsId: { eq: id },
        ...patternQueryStatus,
      },
    }).subscribe({
      next: ({ items }) => {
        items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setPatterns([...items]);
        markLoading("patterns", false);
      },
    });
    return () => {
      danceStyleSub.unsubscribe();
      patternsSub.unsubscribe();
    };
  }, [id, groups]);

  return (
    <React.Fragment>
      {Object.values(loadingMap).some((loading) => loading) ? (
        <LoadingDialog loadingMap={loadingMap} />
      ) : (
        <Flex direction="column" gap="1rem">
          <Helmet>
            <title>{danceStyleRecord.name}</title>
          </Helmet>
          <Flex marginTop={"1rem"}>
            <Heading level={1}>{danceStyleRecord.name}</Heading>
          </Flex>
          <Flex>
            <Link onClick={() => navigate(-1)}>
              <FaArrowLeft aria-label={I18n.get("Back")} />
            </Link>

            {hasAccess(groups, "dance-styles", "update") && (
              <Link href={`/dance-styles/${danceStyleRecord.id}/update`}>
                <FaPencil aria-label={I18n.get("Edit")} />
              </Link>
            )}
          </Flex>
          <Flex direction="column">
            <Flex>
              <Heading>{I18n.get("Discipline")}</Heading>
              <Text>{danceStyleRecord.discipline}</Text>
            </Flex>
            <Flex>
              <Heading>{I18n.get("Beats Per Bar")}</Heading>
              <Text>{danceStyleRecord.beatsPerBar}</Text>
            </Flex>
            <Flex>
              <Heading>{I18n.get("Status")}</Heading>
              <Text>
                {danceStyleRecord.status
                  ? I18n.get("Active")
                  : I18n.get("Disabled")}
              </Text>
            </Flex>
          </Flex>
          <Flex>
            <Heading level={2}>{I18n.get("Patterns")}</Heading>
          </Flex>

          {hasAccess(groups, "patterns", "create") && (
            <Flex>
              <Link onClick={onClickAddPattern}>
                <FaPlus aria-label={I18n.get("Add Pattern")} />
              </Link>
            </Flex>
          )}
          <Grid>
            <Collection
              items={patterns}
              type="list"
              direction="row"
              wrap="wrap"
              alignContent="flex-start"
              searchPlaceholder={I18n.get("Type to search...")}
              isPaginated
              isSearchable
            >
              {(pattern: Pattern) => (
                <Card
                  key={pattern.id}
                  title={pattern.name}
                  variation="outlined"
                  borderRadius="medium"
                  alignSelf="flex-start"
                  width={{ base: "100%", medium: "48.8%", large: "18.9%" }}
                >
                  <Flex
                    direction="column"
                    marginBottom="1rem"
                    minHeight={{ base: "2lh", large: "3lh" }}
                    textAlign="center"
                  >
                    <Heading>
                      <Link href={`/patterns/${pattern.id}/view`}>
                        {pattern.name}
                      </Link>
                      {hasAccess(groups, "patterns", "update") && (
                        <>
                          &nbsp;{" "}
                          <Link href={`/patterns/${pattern.id}/update`}>
                            <FaPencil aria-label={I18n.get("Edit")} />
                          </Link>
                        </>
                      )}
                    </Heading>
                  </Flex>
                  <Flex
                    direction="row"
                    wrap="wrap"
                    justifyContent="center"
                    marginBottom="1rem"
                  >
                    <Badge backgroundColor={"blue.40"}>
                      #{pattern.order && pattern.order / 10}
                    </Badge>
                    <Badge
                      backgroundColor={pattern.status ? "green.40" : "gray.40"}
                    >
                      {pattern.status
                        ? I18n.get("Active")
                        : I18n.get("Disabled")}
                    </Badge>
                  </Flex>
                </Card>
              )}
            </Collection>
          </Grid>
          <Dialog
            open={open}
            onClose={onCloseDialog}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
          >
            <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
              Add Pattern
            </DialogTitle>
            <DialogContent>
              <PatternCreateForm
                danceStyles={[danceStyleRecord]}
                selectedDanceStylePatternsId={danceStyleRecord.id}
              />
            </DialogContent>
          </Dialog>
        </Flex>
      )}
    </React.Fragment>
  );
};

export default DanceStyleView;
